import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { AuthGuard } from "./components/auth/AuthGuard";
import { PasswordRecoveryGate } from "./components/auth/PasswordRecoveryGate";
import { ErrorBoundary } from "./components/error/ErrorBoundary";
import { GlobalErrorHandler } from "./components/error/GlobalErrorHandler";
import { OfflineIndicator } from "./components/ui/offline-indicator";
import { initSentry, logError, logEvent } from './lib/sentry';
import { analytics } from './lib/analytics';
import { SecurityProvider } from './components/security/SecurityProvider';
import { useAnalytics } from './hooks/useAnalytics';
import { PerformanceMonitor } from "./components/performance/PerformanceMonitor";
import { AnalyticsProvider } from "./components/analytics/AnalyticsProvider";
import { OnboardingProvider } from './contexts/OnboardingContext';
import { OnboardingTour } from './components/onboarding/OnboardingTour';
import { lazy, Suspense } from "react";
import { LoadingSkeleton } from "./components/ui/loading-skeleton";

// Lazy load pages for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignWizard = lazy(() => import("./pages/CampaignWizard"));
const CampaignDetails = lazy(() => import("./pages/CampaignDetails"));
const CampaignEdit = lazy(() => import("./pages/CampaignEdit"));
const Leads = lazy(() => import("./pages/Leads"));
const LeadView = lazy(() => import("./pages/LeadView"));
const Agents = lazy(() => import("./pages/Agents"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const CallLogs = lazy(() => import("./pages/CallLogs"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const Billing = lazy(() => import("./pages/Billing"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Auth = lazy(() => import("./pages/Auth"));
const Terms = lazy(() => import("./pages/Terms"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Calendar = lazy(() => import("./pages/Calendar"));
const CalendarOAuthCallback = lazy(() => import("./pages/CalendarOAuthCallback"));
const ShopifyOAuthCallback = lazy(() => import("./pages/ShopifyOAuthCallback"));
const BrandIntelligence = lazy(() => import("./pages/BrandIntelligence"));
const BrandView = lazy(() => import("./pages/BrandView"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminConsentDeclaration = lazy(() => import("./pages/AdminConsentDeclaration"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const DemoWidget = lazy(() => import("./pages/DemoWidget"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const Widget = lazy(() => import("./pages/Widget"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      onError: (error: any) => {
        logError(error, { context: 'mutation' });
      },
    },
  },
});

// Initialize services
initSentry();
analytics.init();

import { ThemeProvider } from "@/components/theme-provider";
import { useLocation } from "react-router-dom";

const ThemeProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  // Force light mode on auth pages and public widget
  const forceLightMode = location.pathname.startsWith('/auth')
    || location.pathname.startsWith('/reset-password')
    || location.pathname.startsWith('/widget/');

  return (
    <ThemeProvider
      defaultTheme="dark"
      storageKey="vite-ui-theme"
      attribute="class"
      forcedTheme={forceLightMode ? 'light' : undefined}
    >
      {children}
    </ThemeProvider>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <SecurityProvider>
          <OnboardingProvider>
            <BrowserRouter>
              <ThemeProviderWrapper>
                <GlobalErrorHandler />
                <OfflineIndicator />
                <PerformanceMonitor />
                <AnalyticsProvider>
                  <OnboardingTour />
                  <PasswordRecoveryGate>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route
                      path="/auth"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <Auth />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/terms"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <Terms />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reset-password"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <ResetPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/auth/calendar-callback"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <CalendarOAuthCallback />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/shopify/callback"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <ShopifyOAuthCallback />
                        </Suspense>
                      }
                    />
                    <Route element={
                      <AuthGuard>
                        <Layout />
                      </AuthGuard>
                    }>
                      <Route
                        path="/dashboard"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Dashboard />}>
                            <Dashboard />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/campaigns"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Table />}>
                            <Campaigns />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/campaigns/new"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Form />}>
                            <CampaignWizard />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/campaigns/:id"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <CampaignDetails />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/campaigns/:id/edit"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Form />}>
                            <CampaignEdit />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/leads"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Table />}>
                            <Leads />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/leads/:id"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <LeadView />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/brand-intelligence/:id"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <BrandView />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/agents"
                        element={
                          <Suspense fallback={<LoadingSkeleton.List />}>
                            <Agents />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/get-started"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <GetStarted />
                          </Suspense>
                        }
                      />
                      <Route path="/voice-agents" element={<Navigate to="/agents" replace />} />
                      <Route
                        path="/call-logs"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Table />}>
                            <CallLogs />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/calendar"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <Calendar />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Form />}>
                            <Settings />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Form />}>
                            <Profile />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/billing"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <Billing />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Dashboard />}>
                            <Analytics />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/compliance"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <Compliance />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/brand-intelligence"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Dashboard />}>
                            <BrandIntelligence />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Dashboard />}>
                            <Admin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin/consent-declarations/:id"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <AdminConsentDeclaration />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/api-docs"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Page />}>
                            <ApiDocs />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/demo-widget"
                        element={
                          <Suspense fallback={<LoadingSkeleton.Dashboard />}>
                            <DemoWidget />
                          </Suspense>
                        }
                      />
                    </Route>

                    {/* Public demo route - no auth required */}
                    <Route
                      path="/demo/:slug"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <DemoPage />
                        </Suspense>
                      }
                    />
                    {/* Public widget route - no auth required */}
                    <Route
                      path="/widget/:widgetKey"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <Widget />
                        </Suspense>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <Suspense fallback={<LoadingSkeleton.Page />}>
                          <NotFound />
                        </Suspense>
                      }
                    />
                  </Routes>
                  </PasswordRecoveryGate>
                </AnalyticsProvider>
              </ThemeProviderWrapper>
            </BrowserRouter>
          </OnboardingProvider>
        </SecurityProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary >
);

export default App;
